import { useQuery } from '@tanstack/react-query'
import { ClipboardList } from 'lucide-react'
import { convictionApi } from '../api/client'
import type { ScorecardPosition } from '../types'
import { METRICS } from '../lib/metrics'
import { InfoTip } from './InfoTip'
import './ScorecardTable.css'

interface ScorecardTableProps {
  accountId: number
  mirrorsSourceId?: number | null
}

function fmtPrice(v: number | null): string {
  if (v === null) return '—'
  return `$${v.toFixed(2)}`
}

function fmtExcess(v: number | null): { text: string; cls: string } {
  if (v === null) return { text: '—', cls: 'muted' }
  const pct = v * 100
  const sign = pct >= 0 ? '+' : ''
  return { text: `${sign}${pct.toFixed(2)}%`, cls: pct >= 0 ? 'delta-up' : 'delta-down' }
}

function ScorecardRow({ pos }: { pos: ScorecardPosition }) {
  const excess = fmtExcess(pos.excess_vs_spy)
  return (
    <tr className="scorecard-row">
      <td className="mono scorecard-symbol scorecard-cell-symbol">{pos.symbol}</td>
      <td className="scorecard-cell-sector">{pos.sector ?? <span className="muted">—</span>}</td>
      <td className="scorecard-cell-state">
        <span className={`scorecard-state-badge scorecard-state-badge--${pos.state}`}>
          {pos.state}
        </span>
      </td>
      <td className="scorecard-entry scorecard-cell-entry" data-label="Entry">
        <span>{pos.entry_date ?? '—'}</span>
        {pos.entry_price !== null && <span className="num scorecard-entry-price">{fmtPrice(pos.entry_price)}</span>}
      </td>
      <td className="num scorecard-cell-trend" data-label={METRICS.trend_excess_126d.short}>
        {pos.sue === null ? <span className="muted">—</span> : pos.sue.toFixed(1)}
      </td>
      <td className="scorecard-cell-reason" data-label="Reason">
        {pos.state === 'exited' && pos.reason ? (
          <span className={`scorecard-reason-chip ${pos.reason === 'thesis_break' ? 'scorecard-reason-chip--break' : ''}`}>
            {pos.reason.replace(/_/g, ' ')}
          </span>
        ) : (
          <span className="muted">—</span>
        )}
      </td>
      <td className={`num ${excess.cls} scorecard-cell-excess`}>{excess.text}</td>
    </tr>
  )
}

/**
 * Core evidence table for a book's conviction picks: held positions first,
 * then exited, with SUE, entry, and excess-vs-SPY-from-entry columns.
 */
export function ScorecardTable({ accountId, mirrorsSourceId }: ScorecardTableProps) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['scorecard', accountId],
    queryFn: () => convictionApi.scorecard(accountId),
    retry: 1,
    refetchInterval: 30000,
  })

  const positions = data?.positions ?? []
  const held = positions.filter((p) => p.state === 'held')
  const exited = positions.filter((p) => p.state !== 'held')
  const ordered = [...held, ...exited]

  return (
    <div className="card scorecard-table">
      <div className="card-header">
        <h3 className="card-title">
          <ClipboardList size={18} className="icon" aria-hidden="true" />
          Scorecard
        </h3>
      </div>

      {isLoading ? (
        <div className="loading"><div className="spinner" /></div>
      ) : isError ? (
        <div className="empty-state">
          <div className="empty-icon"><ClipboardList size={28} aria-hidden="true" /></div>
          <div className="empty-text">scorecard unavailable</div>
        </div>
      ) : ordered.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon"><ClipboardList size={28} aria-hidden="true" /></div>
          <div className="empty-text">
            {mirrorsSourceId != null
              ? `mirrors account ${mirrorsSourceId} — the conviction lifecycle lives on the research book`
              : 'no positions yet'}
          </div>
        </div>
      ) : (
        <div className="scorecard-table-scroll">
          <table className="scorecard-table-el">
            <thead>
              <tr>
                <th scope="col">Symbol</th>
                <th scope="col">Sector</th>
                <th scope="col">State</th>
                <th scope="col">Entry</th>
                <th scope="col">
                  {METRICS.trend_excess_126d.short}
                  <InfoTip metric="trend_excess_126d" />
                </th>
                <th scope="col">Reason</th>
                <th scope="col">
                  {METRICS.excess_vs_spy_since_entry.short}
                  <InfoTip metric="excess_vs_spy_since_entry" />
                </th>
              </tr>
            </thead>
            <tbody>
              {ordered.map((pos) => (
                <ScorecardRow key={`${pos.symbol}-${pos.state}-${pos.entry_date ?? pos.exited_date ?? 'x'}`} pos={pos} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
